import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

export function Login() {
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);
  const user = useAuthStore((s) => s.user);
  const error = useAuthStore((s) => s.error);
  const loading = useAuthStore((s) => s.loading);
  const navigate = useNavigate();

  // If already authenticated, redirect
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleCredentialResponse = useCallback(
    async (response: google.accounts.id.CredentialResponse) => {
      try {
        await loginWithGoogle(response.credential);
        navigate('/', { replace: true });
      } catch {
        // error is set in store
      }
    },
    [loginWithGoogle, navigate]
  );

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.error('VITE_GOOGLE_CLIENT_ID is not set');
      return;
    }

    // Wait for Google Identity Services to load
    const initGoogle = () => {
      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleCredentialResponse,
        auto_select: false,
      });

      google.accounts.id.renderButton(
        document.getElementById('google-signin-btn')!,
        {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'signin_with',
          shape: 'rectangular',
        }
      );
    };

    if (typeof google !== 'undefined' && google.accounts) {
      initGoogle();
    } else {
      // Script may still be loading
      const interval = setInterval(() => {
        if (typeof google !== 'undefined' && google.accounts) {
          clearInterval(interval);
          initGoogle();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [handleCredentialResponse]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 to-brand-100">
      <div className="bg-white rounded-2xl shadow-xl p-10 w-full max-w-md animate-fadeIn">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            IdeaPilot Validation
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Fokussiertes Validierungs-Cockpit
          </p>
        </div>

        {/* Google Button */}
        <div className="flex justify-center mb-6">
          <div id="google-signin-btn" />
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center">
            <div className="animate-spin h-6 w-6 border-3 border-brand-600 border-t-transparent rounded-full" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 text-center">
            {error}
          </div>
        )}

        {/* Info */}
        <p className="text-xs text-gray-400 text-center mt-6">
          Zugang nur für autorisierte Konten.
          <br />
          Kontakt: dritan@mulla-systems.com
        </p>
      </div>
    </div>
  );
}
